import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

const SEO = ({
  title = 'Prepify - #1 NUST Entry Test Preparation Platform',
  description = 'Ace your NUST Entry Test (NET) with Prepify. Interactive chapter-wise tests, mock exams, and analytics for Engineering, CS, and Business.',
  keywords = 'NUST Entry Test, NET Preparation, NUST Past Papers, Online Mock Test, NET Business, NET Engineering, NET Computer Science, NUST University, Entrance Exam, Test Preparation Pakistan',
  image = '/android-chrome-512x512.png',
  url = typeof window !== 'undefined' ? window.location.href : '',
  type = 'website',
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://prepify.com';
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const fullUrl = url.startsWith('http') ? url : `${siteUrl}${url}`;

  // JSON-LD Schema for Educational Organization
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Prepify',
    url: siteUrl,
    description: description,
    logo: `${siteUrl}/android-chrome-512x512.png`,
    sameAs: [
      // Add social media links if available
    ],
    offers: {
      '@type': 'Offer',
      category: 'Educational Service',
      description: 'NUST Entry Test (NET) Preparation Platform',
    },
    audience: {
      '@type': 'EducationalAudience',
      educationalRole: 'student',
      geographicArea: {
        '@type': 'Country',
        name: 'Pakistan',
      },
    },
  };

  // WebSite Schema for better search visibility
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Prepify',
    url: siteUrl,
    description: description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Prepify" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="theme-color" content="#4f46e5" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Prepify" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:creator" content="@Prepify" />
      <meta name="twitter:site" content="@Prepify" />

      {/* Additional SEO Tags */}
      <meta name="geo.region" content="PK" />
      <meta name="geo.placename" content="Pakistan" />
      <link rel="canonical" href={fullUrl} />

      {/* JSON-LD Schema */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
};

export default SEO;

